import dayjs from "dayjs";
import { supabase } from "../../config/supabaseClient";
import { UpdateCombinedText } from "../SupabaseServices/ContentDatabase";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { TRACKING_TYPES } from "../SupabaseServices/Track";
import trackEmbed, {
  embedAnalyticsPathname,
  TRACKING_EMBED_TYPES,
} from "../SupabaseServices/TrackEmbed";
import trackTemplate, {
  TEMPLATE_TRACKING_TYPES,
} from "../SupabaseServices/TrackTemplate";
import { TEMPLATE_ANALYTICS_EVENTS } from "../SupabaseServices/templateAnalyticsEvents";
import {
  getOpenCollectionIn,
  resolveOpenCollectionIn,
  shouldOpenCollectionInNewWindow,
  type OpenCollectionIn,
} from "../embed/openCollectionNavigation";
import { requestEmbedIframeHeightRecalcFor } from "../HelperFunction/Useiframeheight";
import track from "../SupabaseServices/Track";
import { carouselTrackIdentity } from "../analytics/carouselAnalyticsKey";
import {
  legacyLibAnalyticsPathname,
  shouldMirrorLegacyLibAnalytics,
} from "../analytics/legacyLibAnalyticsPath";
import { isPublicWebsiteTemplateRoute } from "../websiteTemplateGuards";
import { resolveTemplatePageKeyFromPathname } from "../../Screens/DashboardView/MasterTemplateEditor/websitePublicTemplatePath";
import { getWeekDate } from "../HelperFunction/TimezoneDates";
import {
  buildCollectionViewAllDestination,
  resolveDashboardCollectionFrom,
  resolveDashboardCreatorPageSlug,
  toAbsolutePublicUrl,
} from "../url/buildCollectionViewAllDestination";
import type { CreatorBrowsableUrlPrefs } from "../url/getCreatorPublicBaseUrl";
import { keepNavigationOnVercelDeploy } from "../url/getCreatorPublicBaseUrl";
import { isVercelDeploymentHost } from "../url/creatorHost";
import { navigateWithViewTransitionAndSmoothScroll } from "../viewTransitions";
import { getComponentsTableName } from "../componentsTable";
import type { PersistedImageCrop } from "../imageCrop/cropStatePersist";
import { isMissingImageCropColumnError } from "../imageCrop/cropStatePersist";
import { darkenColor } from "../../Screens/DashboardView/StayUpToDate/LandingPageComponents/LinksComponent/Components/hoverUtils";
import { resolveShopButtonCta } from "../normalizeShopUrl";
import { isHexDark } from "../HelperFunction/HelperFunction";
import { isAffiliateLocked, isStarterPlanValue } from "../subscriptionPlan";
import {
  isUsableRemoteMediaUrl,
  pickBestStillImageUrl,
  splitMediaUrls,
} from "../media/feedMedia";

dayjs.extend(utc);
dayjs.extend(timezone);
const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const lightenColor = (color: string, percent: number) => {
  const hex = color.replace("#", "");

  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const newR = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  const newG = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  const newB = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

  return `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
};

export const daysSinceMultipleDays = (targetDaysOfWeek: number[]) => {
  const currentDayOfWeek = getWeekDate();
  const daysSinceTargetDays = targetDaysOfWeek.map(
    (targetDayOfWeek) => (currentDayOfWeek - targetDayOfWeek + 7) % 7,
  );
  return daysSinceTargetDays;
};

export const isNavigable = (component: any) => {
  const displayName = component.display_name || "";
  return (
    displayName !== "My Links" &&
    !displayName.includes("Form") &&
    component.type !== "banner" &&
    component.type !== "highlight" &&
    component.type !== "links-section" &&
    component.type !== "embed-form" &&
    component.type !== "newsletter-form" &&
    !displayName.includes("Newsletter") &&
    component.type !== "campaign" &&
    component.type !== "store-product" &&
    component.type !== "product-collection" &&
    component.type !== "carousel-collection" &&
    component.type !== "latest-podcast"
  );
};

export function redirectToUrl(data: any, isLinks: any) {
  // Same Shop → View → Watch priority as ProductShopButton / homepage pills
  // (skips social profile mentions in tagged/external).
  const cta = resolveShopButtonCta(data, undefined, {
    forceShopLabel: Boolean(isLinks),
  });
  const redirectUrl = cta?.url || "";
  return addHttpToLink(redirectUrl);
}
export async function updateIsEmptyStatus(
  name: any,
  user_id: any,
  id: any,
  from: any,
) {
  const tableName = getComponentsTableName(from);
  const { data, error } = await supabase
    .from(tableName)
    .update({ isEmpty: true })
    .eq("name", name)
    .eq("id", id)
    .eq("user_id", user_id)
    .neq("status", "deleted");
}

/** Undo a stale empty mark once a public strip actually has posts. */
export async function clearIsEmptyStatus(
  name: any,
  user_id: any,
  id: any,
  from: any,
) {
  const tableName = getComponentsTableName(from);
  await supabase
    .from(tableName)
    .update({ isEmpty: false })
    .eq("name", name)
    .eq("id", id)
    .eq("user_id", user_id)
    .neq("status", "deleted");
}
export async function updateCardImage(
  name: any,
  user_id: any,
  id: any,
  image: any,
  from: any,
  extras?: {
    /** Uncropped source for crop restore. Omitted = leave DB column unchanged. */
    originalImageUrl?: string | null;
    /** Crop restore payload. Omitted = leave DB column unchanged. */
    imageCrop?: PersistedImageCrop | null;
    markCardImageUpdated?: boolean;
    /** Legacy alias used by website-template editors. */
    isCardImageUpdated?: boolean;
  },
) {
  const tableName = getComponentsTableName(from);
  const payload: Record<string, unknown> = {
    card_image: image,
    isEmpty: false,
  };

  // Only touch original_banner_image when explicitly provided so auto-fill
  // paths that call updateCardImage(...) keep any existing original intact.
  if (extras && "originalImageUrl" in extras) {
    payload.original_banner_image = extras.originalImageUrl || null;
  }
  if (extras && "imageCrop" in extras) {
    payload.image_crop = extras.imageCrop ?? null;
  }
  if (extras?.markCardImageUpdated) {
    payload.is_card_image_updated = true;
  } else if (extras?.isCardImageUpdated !== undefined) {
    payload.is_card_image_updated = extras.isCardImageUpdated;
  }

  let { error } = await supabase
    .from(tableName)
    .update(payload)
    .eq("id", id)
    .eq("user_id", user_id)
    .neq("status", "deleted");

  if (error && isMissingImageCropColumnError(error) && "image_crop" in payload) {
    const { image_crop: _omit, ...withoutCrop } = payload;
    ({ error } = await supabase
      .from(tableName)
      .update(withoutCrop)
      .eq("id", id)
      .eq("user_id", user_id)
      .neq("status", "deleted"));
  }

  if (error) {
    console.error("updateCardImage failed", { tableName, id, error, payload });
    throw error;
  }
}

export function getMediaSrc(
  type: any,
  data: any,
  isCarousel: any,
  media_url: any,
) {
  if (isCarousel) {
    return media_url || pickBestStillImageUrl([data?.media_url]) || "";
  }
  if (type === "product-bank") {
    return (
      pickBestStillImageUrl([
        data?.media_url,
        data?.product_image,
        data?.table_image,
        data?.thumbnail,
      ]) || ""
    );
  }
  // Prefer durable stills (storage) over expired Instagram CDN product_image.
  // Never return a video URL for <img>.
  return (
    pickBestStillImageUrl([
      data?.storage_url,
      data?.media_url,
      media_url,
      data?.thumbnail,
      data?.thumbnail_url,
      data?.card_image,
      data?.table_image,
      data?.product_image,
    ]) || ""
  );
}

export const testLink = (link: any) => {
  const url = link;
  if (url && url !== "") {
    // const validUrl = url.startsWith("http") ? url : `http://${url}`;
    const validUrl = addHttpToLink(url);
    window.open(validUrl, "_blank");
  }
};

export async function updateNameValues(
  username: string,
  type: any,
  post_id: any,
  user_id: any,
  value: any,
) {
  if (type === "content" || type === "query-content") {
    const { data: user, error } = await supabase
      .from(`${username}`)
      .update({ content_title: value })
      .eq("post_id", post_id)
      .eq("user_id", user_id);
    const { data: newProduct, error: newProductErroor } = await supabase
      .from("product_bank")
      .update({ content_title: value })
      .eq("product_id", post_id)
      .eq("user_id", user_id);
  } else {
    const { data: user, error } = await supabase
      .from(`${username}`)
      .update({ shop_title: value, product_name: value })
      .eq("post_id", post_id)
      .eq("user_id", user_id);
    const { data: newProduct, error: newProductErroor } = await supabase
      .from("product_bank")
      .update({ shop_title: value })
      .eq("product_id", post_id)
      .eq("user_id", user_id);
  }

  const { data: newContent, error: newErroor } = await supabase
    .from("content_new")
    .update({ product_name: value })
    .eq("post_id", post_id)
    .eq("user_id", user_id);
  await UpdateCombinedText(username, user_id, post_id, {
    preserveExistingSearchText: true,
  });
}

export const formatDateToString = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

// Function to get all dates from a day selection (handles "This Weekend" specially)
export const getAllDatesFromSelection = (selectedDays: string[]): string[] => {
  const allDates: string[] = [];

  selectedDays.forEach((day) => {
    const dateResult = getMostRecentDayDate(day);

    if (dateResult) {
      if (Array.isArray(dateResult)) {
        // For "This Weekend", add both Saturday and Sunday dates
        dateResult.forEach((date) => {
          allDates.push(formatDateToString(date));
        });
      } else {
        allDates.push(formatDateToString(dateResult));
      }
    }
  });

  // Remove duplicates and sort in ascending order
  return [...new Set(allDates)].sort();
};

export const getMostRecentDayDate = (dayName: string): Date | Date[] | null => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const today = new Date();
  const todayDayIndex = today.getDay(); // 0-6, where 0 is Sunday

  // Handle "This Weekend" specially - return both Saturday and Sunday
  if (dayName === "This Weekend") {
    // For "This Weekend", return an array of [Saturday, Sunday]
    const lastWeekend = [];

    // Get the most recent Saturday
    const lastSaturday = new Date(today);
    if (todayDayIndex === 0) {
      // Today is Sunday
      lastSaturday.setDate(today.getDate() - 1); // Yesterday was Saturday
    } else if (todayDayIndex === 6) {
      // Today is Saturday
      // Keep today's date for Saturday
    } else {
      // Set to last Saturday
      lastSaturday.setDate(today.getDate() - todayDayIndex - 1);
    }

    // Get the most recent Sunday
    const lastSunday = new Date(today);
    if (todayDayIndex === 0) {
      // Today is Sunday
      // Keep today's date for Sunday
    } else {
      // Set to last Sunday
      lastSunday.setDate(today.getDate() - todayDayIndex);
    }

    return [lastSaturday, lastSunday];
  }

  const targetDayIndex = days.indexOf(dayName);
  if (targetDayIndex === -1) return null;

  let daysAgo = todayDayIndex - targetDayIndex;
  if (daysAgo < 0) {
    daysAgo += 7; // If the day is ahead in the week, go back to previous week
  }

  const recentDate = new Date(today);
  recentDate.setDate(today.getDate() - daysAgo);

  return recentDate;
};

export const addHttpToLink = (link: string) => {
  if (!link) return "";
  const trimmed = String(link).trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Protocol-relative CDN URLs
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  // Root-relative paths must stay same-origin — do NOT invent `https:///…`
  // (that often resolves to the current dashboard URL in the browser).
  if (trimmed.startsWith("/")) return trimmed;
  return `https://${trimmed}`;
};

/**
 * Open an outbound URL in a new tab using a real `<a>` click.
 * `window.open` is often blocked on real mobile browsers (esp. Android /
 * in-app WebViews), which left only Shop `<a href>` pills tappable while
 * the rest of the post looked dead.
 */
export function openOutboundUrl(raw: string | undefined | null): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }
  const href = addHttpToLink(String(raw || "").trim());
  if (!href || href === "#") return false;

  const a = document.createElement("a");
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.setAttribute("aria-hidden", "true");
  // iOS Safari needs the node in the document for the gesture to count.
  a.style.position = "fixed";
  a.style.left = "-9999px";
  a.style.top = "0";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return true;
}

export const validateAndFormatUrl = (link: string) => {
  const trimmed = link?.trim() || "";

  if (!trimmed) {
    return {
      isValid: false,
      formatted: "",
      error: "Link is required.",
    };
  }

  try {
    const formatted = addHttpToLink(trimmed);
    const url = new URL(formatted);

    if (!url.hostname || !url.hostname.includes(".")) {
      throw new Error("Invalid host");
    }

    return {
      isValid: true,
      formatted,
      error: "",
    };
  } catch (_err) {
    return {
      isValid: false,
      formatted: "",
      error: "Please enter a valid URL (e.g., example.com or https://example.com)",
    };
  }
};

export function formatPostDate(utcDateStr: any) {
  const userTZ = userTimeZone;

  // Format dates for different timezones
  const utcDate = dayjs.utc(utcDateStr);

  const formattedDates = {
    utc: utcDate.format("MMM D, YYYY"),
    local: utcDate.tz(userTZ).format(`MMM D, YYYY`),
    ist: utcDate.tz("Asia/Kolkata").format("MMM D, YYYY"),
    newYork: utcDate.tz("America/New_York").format("MMM D, YYYY"),
  };

  // --------old way --------
  {
    /* {new Intl.DateTimeFormat("en-US", {
                                year: "numeric",
                                day: "numeric",
                                month: "long",
                              }).format(new Date(image.post_date))} */
  }
  // ------------

  return formattedDates.local;
}

export const DuplicateNameError = () => {
  return "That name's already taken. Try something unique to avoid confusion.";
};

export const reactQueryParams = () => {
  return {
    // retry: 1,
    refetchOnWindowFocus: false,
  };
};

export const handleNavigate = (
  navigate: any,
  location: any,
  username: any,
  user_id: any,
  component: any,
  from: any,
  openCollectionIn?: OpenCollectionIn,
  creatorPageSlug?: string | null,
  customDomain?: string | null,
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null,
) => {
  if (isNavigable(component)) {
    const componentSlug = component.route_name || component.unique_id;
    const effectiveFrom =
      from || resolveDashboardCollectionFrom(location?.pathname) || undefined;
    const resolvedCreatorPageSlug =
      creatorPageSlug ??
      component?.creatorPageSlug ??
      resolveDashboardCreatorPageSlug(location?.pathname) ??
      null;
    const destination = keepNavigationOnVercelDeploy(
      buildCollectionViewAllDestination({
        username,
        componentSlug,
        from: effectiveFrom,
        location,
        creatorPageSlug: resolvedCreatorPageSlug,
        customDomain,
        browsableUrlPrefs,
        component,
      }),
    );

    const resolvedOpenCollectionIn = resolveOpenCollectionIn(
      openCollectionIn ?? getOpenCollectionIn()
    );
    const useNewWindow = shouldOpenCollectionInNewWindow(
      effectiveFrom,
      resolvedOpenCollectionIn,
      location?.pathname,
    );

    navigateWithViewTransitionAndSmoothScroll(() => {
      if (useNewWindow) {
        window.open(
          toAbsolutePublicUrl(destination),
          "_blank",
          "noopener,noreferrer",
        );
        return;
      }

      // Relative SPA paths — unchanged production behavior.
      if (!/^https?:\/\//i.test(destination)) {
        navigate(destination, {
          state: { data: component, from: effectiveFrom },
        });
        return;
      }

      // Absolute URLs: only convert to in-app navigate when same-origin
      // (or Vercel rewrite). Cross-origin custom domains must full-navigate.
      try {
        const url = new URL(destination);
        const sameOrigin =
          typeof window !== "undefined" &&
          url.origin === window.location.origin;
        if (sameOrigin || isVercelDeploymentHost()) {
          navigate(`${url.pathname}${url.search}${url.hash}`, {
            state: { data: component, from: effectiveFrom },
          });
        } else {
          window.location.href = destination;
        }
      } catch {
        window.location.href = destination;
      }
    }, window);

    if (!useNewWindow) {
      requestEmbedIframeHeightRecalcFor(effectiveFrom);
    }

    const clickDetails = {
      ...carouselTrackIdentity(component),
    };
    const titleClick = {
      ...clickDetails,
      post_id: `title:${clickDetails.component_id || clickDetails.unique_id || "section"}`,
      name: clickDetails.title || "Title",
      platform: clickDetails.type || "title",
    };
    const titlePageClickId =
      clickDetails.component_id || clickDetails.unique_id || "";
    const titlePageClick = titlePageClickId
      ? { ...titleClick, id: titlePageClickId }
      : null;
    const path = location?.pathname || "";
    if (from === "embed-search") {
      const embedPath = embedAnalyticsPathname(username);
      trackEmbed("Carousel Click", TRACKING_EMBED_TYPES.EVENT, {
        pathname: embedPath,
        details: clickDetails,
        user_id: user_id,
        title: "Stay Up To Date",
      });
      trackEmbed("Post Click", TRACKING_EMBED_TYPES.EVENT, {
        pathname: embedPath,
        details: titleClick,
        user_id: user_id,
        title: "Stay Up To Date",
      });
    } else if (isPublicWebsiteTemplateRoute(path)) {
      const identity = clickDetails;
      const pageKey =
        resolveTemplatePageKeyFromPathname(path) || "linkInBio";
      trackTemplate(TEMPLATE_ANALYTICS_EVENTS.CAROUSEL_TAB, TEMPLATE_TRACKING_TYPES.EVENT, {
        pathname: path,
        details: identity,
        user_id,
        username,
        page_key: pageKey,
        entity_type: "carousel_tab",
        entity_label: component?.name,
        entity_id: identity.component_id || identity.unique_id,
        carousel_slug: identity.component_id || identity.unique_id,
      });
      if (shouldMirrorLegacyLibAnalytics(path, pageKey)) {
        const libPath = legacyLibAnalyticsPathname(username);
        track("Carousel Click", TRACKING_TYPES.EVENT, {
          pathname: libPath,
          details: titleClick,
          user_id: user_id,
          title: "Stay Up To Date",
        });
        if (titlePageClick) {
          track("Post Click", TRACKING_TYPES.EVENT, {
            pathname: libPath,
            details: titlePageClick,
            user_id: user_id,
            title: "Stay Up To Date",
          });
        }
      }
    } else {
      const libPath = legacyLibAnalyticsPathname(username);
      track("Carousel Click", TRACKING_TYPES.EVENT, {
        pathname: libPath,
        details: titleClick,
        user_id: user_id,
        title: "Stay Up To Date",
      });
      if (titlePageClick) {
        track("Post Click", TRACKING_TYPES.EVENT, {
          pathname: libPath,
          details: titlePageClick,
          user_id: user_id,
          title: "Stay Up To Date",
        });
      }
    }
  }
};

/** First non-empty URL from a comma-separated list, array, or common object shapes. */
export function pickFirstNonEmptyImageField(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) return pickFirstNonEmptyImageField(value[0]);
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    return (
      pickFirstNonEmptyImageField(o.url) ||
      pickFirstNonEmptyImageField(o.src) ||
      pickFirstNonEmptyImageField(o.thumbnail) ||
      pickFirstNonEmptyImageField(o.thumbnailUrl) ||
      ""
    );
  }
  if (typeof value !== "string") return "";
  // Prefer safe split so signed CDN query commas are not truncated; reject
  // ephemeral blob:/data: previews that break as public <img src>.
  for (const part of splitMediaUrls(value)) {
    const t = part.trim();
    if (t && isUsableRemoteMediaUrl(t)) return t;
  }
  return "";
}

/**
 * Returns the first best-available image URL for card/preview use.
 * Prefers explicit card/table/product images, then thumbnail, then media URLs.
 */
export const getFirstUsableImageUrlForCard = (item: any): string => {
  const candidate = item || {};
  return (
    pickBestStillImageUrl([
      candidate.storage_url,
      candidate.card_image,
      candidate.table_image,
      candidate.thumbnail,
      candidate.thumbnail_url,
      candidate.media_url,
      candidate.product_image,
    ]) || ""
  );
};

type ShopParityStyleArgs = {
  designTokens?: any;
  brand_color?: string;
  contextButtonBackground?: string;
};

/**
 * Generates follower CTA parity styles used across campaign/store forms.
 */
export const getShopParityButtonStyle = ({
  designTokens,
  brand_color,
  contextButtonBackground,
}: ShopParityStyleArgs) => {
  const templateName = String(designTokens?.templateName || "").toLowerCase();
  const isDefaultTheme = templateName === "default";
  // Match ProductShopButton / .shop-link: fill uses forthColor (card/button surface).
  const backgroundColor =
    contextButtonBackground ||
    designTokens?.forthColor ||
    brand_color ||
    (isDefaultTheme ? "#e6e7eb" : "#000");
  // Match ProductShopButton text: black on default, secondaryColor otherwise.
  const color = isDefaultTheme
    ? "#000000"
    : designTokens?.secondaryColor || "#ffffff";
  // Match ProductShopButton.getHoverBg exactly.
  const normalizedBg = (backgroundColor || "")
    .replace(/!important/gi, "")
    .trim()
    .toLowerCase();
  const hoverBg = isDefaultTheme
    ? "#d1d2d5"
    : normalizedBg.startsWith("#")
      ? isHexDark(backgroundColor)
        ? lightenColor(backgroundColor, 20)
        : darkenColor(backgroundColor, 10)
      : "#d1d2d5";

  return {
    backgroundColor,
    color,
    hoverBg,
    structural: {
      backgroundColor,
      borderColor: backgroundColor,
      color,
      fontWeight: 600,
      borderRadius: 8,
      border: "none",
      boxShadow: "none",
    } as const,
  };
};

export async function handleFirstImage(
  name: any,
  id: any,
  firstElement: any,
  user_id: any,
  from: any,
) {
  const normalizeImageUrl = (url: string): string => {
    const trimmed = url.trim();
    // Avoid mixed-content in production (https site + http image => blocked)
    if (trimmed.startsWith("http://")) {
      return `https://${trimmed.slice("http://".length)}`;
    }
    return trimmed;
  };

  let imageToSend = getFirstUsableImageUrlForCard(firstElement);
  if (imageToSend) {
    imageToSend = String(imageToSend).split(",")[0];
  }
  if (!imageToSend) return;

  imageToSend = normalizeImageUrl(imageToSend);
  const tableName = getComponentsTableName(from);
  const { data: componentData, error } = await supabase
    .from(tableName)
    .select("id,is_card_image_updated,card_image")
    .eq("id", id)
    .eq("user_id", user_id)
    .neq("status", "deleted")
    .maybeSingle();

  if (error || !componentData) return;

  // Auto-fill card_image until the creator uploads a custom cover (`is_card_image_updated`).
  if (componentData.is_card_image_updated !== true) {
    const existing = pickFirstNonEmptyImageField(componentData.card_image);
    // Skip no-op writes when the stored cover already matches the first feed tile.
    if (existing === imageToSend) return;
    try {
      await updateCardImage(name, user_id, id, imageToSend, from);
    } catch {
      // Best-effort auto cover — never block feed render on a cover write failure.
    }
  }
}

export const getPlaceholderText = (
  name: any,
  hasConnectedContentPlatform: any,
  hasConnectedAffiliatePlatform: any,
  planSource?: any,
) => {
  // Prefer full user row (Stripe via isAffiliateLocked). A bare plan string
  // is only for checkout labels — never treat column leftovers as Stripe.
  const isStarter =
    planSource && typeof planSource === "object"
      ? isAffiliateLocked(planSource)
      : Boolean(planSource) && isStarterPlanValue(planSource);

  if (isStarter) {
    // Starter: content only, never show "products" in placeholder
    if (hasConnectedContentPlatform) {
      return "Search " + name + " content!";
    }
    return "Search My Content";
  }

  // $49/mo and $99/mo (or no plan): content & products
  if (hasConnectedContentPlatform && hasConnectedAffiliatePlatform) {
    return "Search " + name + " content and products!";
  } else if (hasConnectedContentPlatform) {
    return "Search " + name + " content!";
  } else if (hasConnectedAffiliatePlatform) {
    return "Search " + name + " products!";
  } else {
    return "Search My Content & Products";
  }
};
